import { useState, useCallback, useMemo } from 'react'

import { loadStripe, Stripe, StripeElementsOptions } from '@stripe/stripe-js'

import { CreateCardPaymentIntentRequestDTO } from '@/core/api/dtos/request/payment/createCardPaymentIntent'
import { CreateSepaPaymentIntentRequestDTO } from '@/core/api/dtos/request/payment/createSepaPaymentIntent'
import {
	useAvailablePaymentMethods,
	useCreatCardPaymentIntent,
	useCreateSepaPaymentIntent,
} from '@/core/hooks/payment/usePaymentMethods'
import { usePaymentTokens } from '@/core/hooks/payment/usePaymentTokens'
import { useAccountConfig } from '@/core/hooks/useAccountConfig'
import { useTailwindDarkMode } from '@/shared/hooks/useTailwindDarkMode'

// Stripe public keys - these should be provided per billing system (MEX, USA, ESP)
// For now, using placeholders that should be replaced with actual keys
const STRIPE_PUBLIC_KEYS: Record<string, string> = {
	MEX: import.meta.env.VITE_STRIPE_PUBLIC_KEY_MEX || '',
	USA: import.meta.env.VITE_STRIPE_PUBLIC_KEY_USA || '',
	ESP: import.meta.env.VITE_STRIPE_PUBLIC_KEY_ESP || '',
}

interface UseStripePaymentOptions {
	accountId: number
	billingSystemCountry?: string // Used to determine which Stripe key to use
	onError?: (error: any) => void
}

export const useStripePayment = ({
	accountId,
	billingSystemCountry,
	onError,
}: UseStripePaymentOptions) => {
	const [stripePromise, setStripePromise] =
		useState<Promise<Stripe | null> | null>(null)
	const [clientSecret, setClientSecret] = useState<string | null>(null)
	const [paymentPlatformMethodId, setPaymentPlatformMethodId] = useState<
		number | null
	>(null)
	const isDarkMode = useTailwindDarkMode()

	// Get available payment methods
	const { data: availablePaymentMethods, isLoading: isLoadingPaymentMethods } =
		useAvailablePaymentMethods({
			config: useAccountConfig({}),
		})

	// Get payment tokens for the account
	const { data: paymentTokens, isLoading: isLoadingPaymentTokens } =
		usePaymentTokens({
			config: useAccountConfig({}),
		})

	// Determine which Stripe key to use based on billing system country
	const stripePublicKey = useMemo(() => {
		if (!billingSystemCountry) {
			// Default to USA if not specified
			return STRIPE_PUBLIC_KEYS.USA
		}

		const countryUpper = billingSystemCountry.toUpperCase()

		if (countryUpper === 'MX' || countryUpper === 'MEX') {
			return STRIPE_PUBLIC_KEYS.MEX
		}
		if (countryUpper === 'ES' || countryUpper === 'ESP') {
			return STRIPE_PUBLIC_KEYS.ESP
		}
		// Default to USA for US and other countries
		return STRIPE_PUBLIC_KEYS.USA
	}, [billingSystemCountry])

	// Initialize Stripe
	const initializeStripe = useCallback(async () => {
		if (!stripePublicKey) {
			throw new Error('Stripe public key not configured')
		}

		if (!stripePromise) {
			const promise = loadStripe(stripePublicKey)
			setStripePromise(promise)
			return promise
		}

		return stripePromise
	}, [stripePublicKey, stripePromise])

	// Create card payment intent mutation
	const createCardPaymentIntentMutation = useCreatCardPaymentIntent({
		config: useAccountConfig({}),
	})

	// Create SEPA payment intent mutation
	const createSepaPaymentIntentMutation = useCreateSepaPaymentIntent({
		config: useAccountConfig({}),
	})

	// Create card payment intent
	const createCardPaymentIntent = useCallback(
		async (paymentPlatformMethodId: number) => {
			try {
				if (!accountId) {
					throw new Error('Account ID is missing')
				}
				setPaymentPlatformMethodId(paymentPlatformMethodId)
				const data: CreateCardPaymentIntentRequestDTO = {
					account_id: accountId,
					payment_platform_method_id: paymentPlatformMethodId,
				}

				const result = await createCardPaymentIntentMutation.mutateAsync(data)
				const secret = result.client_secret || null
				if (!secret) {
					throw new Error('Stripe client secret not returned')
				}
				setClientSecret(secret)
				return secret
			} catch (error) {
				onError?.(error)
				throw error
			}
		},
		[accountId, createCardPaymentIntentMutation, onError]
	)

	// Create SEPA payment intent
	const createSepaPaymentIntent = useCallback(
		async (paymentPlatformMethodId: number) => {
			try {
				if (!accountId) {
					throw new Error('Account ID is missing')
				}
				setPaymentPlatformMethodId(paymentPlatformMethodId)
				const data: CreateSepaPaymentIntentRequestDTO = {
					account_id: accountId,
					payment_platform_method_id: paymentPlatformMethodId,
				}

				const result = await createSepaPaymentIntentMutation.mutateAsync(data)
				const secret = result.client_secret || null
				if (!secret) {
					throw new Error('Stripe client secret not returned')
				}
				setClientSecret(secret)
				return secret
			} catch (error) {
				onError?.(error)
				throw error
			}
		},
		[accountId, createSepaPaymentIntentMutation, onError]
	)

	// Get Stripe Elements options
	const getElementsOptions = useCallback((): StripeElementsOptions => {
		// When using clientSecret, payment methods are determined by the PaymentIntent on the backend
		// To restrict to card only, ensure the backend PaymentIntent is created with payment_method_types: ['card']
		if (clientSecret) {
			return {
				clientSecret: clientSecret,
				appearance: {
					theme: isDarkMode ? 'night' : 'stripe',
				},
			} as StripeElementsOptions
		}

		// When not using clientSecret, use mode: 'setup' with paymentMethodTypes restriction to card only
		return {
			mode: 'setup',
			paymentMethodTypes: ['card'],
			appearance: {
				theme: isDarkMode ? 'night' : 'stripe',
			},
		} as StripeElementsOptions
	}, [clientSecret, isDarkMode])

	// Reset clientSecret and payment method selection
	const resetPaymentIntent = useCallback(() => {
		setClientSecret(null)
		setPaymentPlatformMethodId(null)
	}, [])

	return {
		// Stripe instance
		initializeStripe,
		stripePromise,
		stripePublicKey,

		// Payment intents
		createCardPaymentIntent,
		createSepaPaymentIntent,
		clientSecret,
		paymentPlatformMethodId,
		resetPaymentIntent,

		// Elements options
		getElementsOptions,

		// Available payment methods
		availablePaymentMethods,
		isLoadingPaymentMethods,

		// Payment tokens
		paymentTokens,
		isLoadingPaymentTokens,

		// Loading states
		isCreatingCardIntent: createCardPaymentIntentMutation.isPending,
		isCreatingSepaIntent: createSepaPaymentIntentMutation.isPending,
		isLoading:
			createCardPaymentIntentMutation.isPending ||
			createSepaPaymentIntentMutation.isPending,

		// Errors
		error:
			createCardPaymentIntentMutation.error ||
			createSepaPaymentIntentMutation.error,
	}
}
